import { Translate } from '@chipinside/translate'
import { Box, Divider } from '@mui/material'
import { Button } from '@mui/material'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { Drawer } from '#/components/Drawer'
import { Icon } from '#/components/Icon'
import { InfoCard } from '#/components/Maps/components/DrawerFetch/card'
import { TextPair } from '#/components/Maps/components/DrawerFetch/text'
import { loadFarm } from '#/store/ducks/farm'
import { usePermissions } from '#/store/ducks/permissions'
import useAppContext from '#/store/hooks/useAppContext'
import { useFormBuilder } from '#/store/hooks/useFormBuilder'
import { numberDisplay } from '#/utils/numberDisplay'

type Farm = {
  code: number
  name: string
  city: string
  state: string
  country: string
  total_count: number
  primary_count: number
  secondary_count: number
}

export default function FarmContent({ data, farm }: { data: Farm; farm: any }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 }}>
      <InfoCard
        title={Translate({ messageKey: 'general_info_plural' })}
        icon={<Icon name="file-text" size={20} />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <TextPair
            label={Translate({ messageKey: 'farm_code' })}
            value={data?.code ?? farm?.code}
          />
          <TextPair label={Translate({ messageKey: 'farm' })} value={data?.name ?? farm?.name} />
          {data ? (
            <TextPair
              label={Translate({ messageKey: 'location' })}
              value={[data?.city, data?.state, data?.country]?.filter(Boolean)?.join(', ')}
            />
          ) : (
            <TextPair
              label={Translate({ messageKey: 'location' })}
              value={[
                farm?.address?.city?.name,
                farm?.address?.city?.state?.abbreviation,
                farm?.address?.city?.state?.country?.name,
              ]
                ?.filter(Boolean)
                ?.join(', ')}
            />
          )}
        </Box>
      </InfoCard>

      {data?.primary_count || data?.secondary_count ? (
        <InfoCard
          title={Translate({ messageKey: 'c_com_plural' })}
          icon={<Icon name="router" size={20} />}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextPair
              label={Translate({ messageKey: 'primary_plural' })}
              value={data?.primary_count}
            />
            <TextPair
              label={Translate({ messageKey: 'secondary_plural' })}
              value={data?.secondary_count}
            />

            <Divider sx={{ my: 1 }} />

            <TextPair label={Translate({ messageKey: 'total' })} value={data?.total_count} />
          </Box>
        </InfoCard>
      ) : null}

      <InfoCard
        title={Translate({ messageKey: 'coordinates' })}
        icon={<Icon name="map-pin" size={20} />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <TextPair label={Translate({ messageKey: 'latitude' })} value={numberDisplay(farm.lat)} />
          <TextPair
            label={Translate({ messageKey: 'longitude' })}
            value={numberDisplay(farm.lng)}
          />
        </Box>
      </InfoCard>
    </Box>
  )
}

export function FarmDrawerFooter({ marker }) {
  const { build } = useFormBuilder()
  const permissions = usePermissions()
  const dispatch = useDispatch()
  const context = useAppContext()

  function updateMarker(marker) {
    Drawer.close()
    build({
      afterSuccess: () => {
        if (marker?.slug && context === 'farm') {
          dispatch(loadFarm({ farmSlug: marker?.slug }))
        }
      },
      formName: 'farm_account',
      formType: 'update',
      params: { farm: marker?.slug, farmName: marker?.name },
    })
  }

  return context !== 'farm' ? (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Button
        variant="outlined"
        fullWidth
        component={Link}
        to={`/farm/${marker.slug}`}
        onClick={() => Drawer.close()}
      >
        {Translate({
          messageKey: 'access_item',
          params: {
            item: Translate({ lower: true, messageKey: 'farm' }),
          },
        })}
      </Button>

      {permissions.system.manage_account_farm && (
        <Button variant="outlined" fullWidth onClick={() => updateMarker(marker)}>
          {Translate({ messageKey: 'update_item' })}
        </Button>
      )}
    </Box>
  ) : null
}
